import {QueryStringFilters} from '~/feature/filter/types';
import {CampaignGroup} from '~/graphql-react-query/graphql';

// aiFuelTypes is not on CampaignGroup yet (backend follow-up). Keep local support so the
// group-by-campaign column/CSV can wire up without empty-type errors until the API ships.
type CampaignInput = CampaignGroup & {aiFuelTypes?: string[]};

export default class Campaign {
    private data: CampaignInput;
    type = 'campaign' as const;

    constructor(input: CampaignInput) {
        this.data = input;
    }

    get campaignName() {
        return this.data.campaignName;
    }

    get csvSafeCampaignName() {
        // campaign names can contain double quotes, so we need to escape them
        return this.data.campaignName.replace(/"/g, '""');
    }

    get status() {
        return this.data.status;
    }

    get aiAdType() {
        return this.data.aiAdType;
    }

    get aiAdvertisers() {
        // we can get empty strings from the AI
        return this.data.aiAdvertisers.filter(Boolean);
    }

    get aiCategories() {
        // we can get empty strings from the AI
        return this.data.aiCategories.filter(Boolean);
    }

    get aiIndustries() {
        // we can get empty strings from the AI
        return this.data.aiIndustries.filter(Boolean);
    }

    get aiProducts() {
        // we can get empty strings from the AI
        return this.data.aiProducts.filter(Boolean);
    }

    get aiFuelTypes() {
        // Empty until groupByCampaign API returns aiFuelTypes
        return (this.data.aiFuelTypes ?? []).filter(Boolean);
    }

    get mediaType() {
        return this.data.mediaType;
    }

    get mediaOwner() {
        return this.data.mediaOwner;
    }

    get publisher() {
        return this.data.publisher;
    }

    get publication() {
        return this.data.publication;
    }

    get region() {
        return this.data.region;
    }

    get firstAppeared(): Date | undefined {
        return this.data.firstAppeared ? new Date(this.data.firstAppeared) : undefined;
    }
    get lastAppeared(): Date | undefined {
        return this.data.lastAppeared ? new Date(this.data.lastAppeared) : undefined;
    }

    get country() {
        return this.data.country ?? 'Australia';
    }

    get thumbnailCreative() {
        return this.data.thumbnailCreative;
    }

    get thumbnailCreativeId() {
        return this.thumbnailCreative.split('.')[0];
    }

    get creativeCount() {
        return this.data.creativeCount;
    }

    get to() {
        const searchParams = new URLSearchParams();
        searchParams.set('currentCampaign', this.campaignName);
        searchParams.set('creativeModalId', this.thumbnailCreativeId);
        searchParams.set('groupByCampaign', 'true');
        const firstAppeared = this.firstAppeared?.toISOString().split('T')[0];
        const lastAppeared = this.lastAppeared?.toISOString().split('T')[0];
        if (firstAppeared && lastAppeared) {
            const filter: QueryStringFilters = {
                creativeDateRange: {
                    absolute: {
                        startDate: firstAppeared,
                        endDate: lastAppeared
                    }
                }
            };
            searchParams.set('filter', JSON.stringify(filter));
        }
        return `/explore-creative?${searchParams.toString()}`;
    }
}
